<script setup>
/* eslint-disable vue/custom-event-name-casing */
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import PipelineAPI from '../../../../../../api/pipelineCards';
import { resolveCardStageName } from '../../../helpers/pipelineStageResolver';
import { getEffectiveCardValue } from '../../../helpers/columnFilterMatchers';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['navigate-to-card', 'navigating']);

const { t, locale } = useI18n();
const store = useStore();
const currentAccount = computed(
  () =>
    store.getters['accounts/getAccount']?.(store.getters.getCurrentAccountId) ||
    store.getters.getCurrentAccount ||
    {}
);
const accountCurrency = computed(() => {
  const currency =
    currentAccount.value.currency || currentAccount.value.settings?.currency;
  return typeof currency === 'object'
    ? currency?.code || 'BRL'
    : currency || 'BRL';
});

const loading = ref(false);
const loadingMore = ref(false);
const relatedCards = ref([]);
const error = ref(null);
const hasMore = ref(false);
const nextCursor = ref(null);
const filterStatus = ref('all');
const filterOptions = computed(() => [
  { value: 'all', label: t('PIPELINE.RELATED.FILTER_ALL') },
  { value: 'open', label: t('PIPELINE.CARD.STATUS.OPEN') },
  { value: 'won', label: t('PIPELINE.CARD.STATUS.WON') },
  { value: 'lost', label: t('PIPELINE.CARD.STATUS.LOST') },
]);
let fetchRequestId = 0;
let fetchController = null;
let paginationContext = null;
let seenCursors = new Set();

const normalizeContextId = value => String(value ?? '').trim();
const currentFetchContext = () => ({
  accountId: normalizeContextId(store.getters.getCurrentAccountId),
  cardId: normalizeContextId(props.item?.id),
  contactId: normalizeContextId(
    props.item?.contact_id || props.item?.contact?.id
  ),
  status: filterStatus.value,
});
const fetchContextMatches = (context, requestId) => {
  const currentContext = currentFetchContext();
  return (
    requestId === fetchRequestId &&
    currentContext.accountId === context.accountId &&
    currentContext.cardId === context.cardId &&
    currentContext.contactId === context.contactId &&
    currentContext.status === context.status
  );
};
const cancelRelatedFetch = () => {
  fetchRequestId += 1;
  fetchController?.abort();
  fetchController = null;
  loading.value = false;
  loadingMore.value = false;
};

const getUserLocale = () => {
  return (locale.value || navigator.language || 'en-US').replace('_', '-');
};

const displayedCount = computed(
  () => `${relatedCards.value.length}${hasMore.value ? '+' : ''}`
);

const statusBadgeClass = card => {
  if (card.won_at) return 'bg-n-teal-3 text-n-teal-11';
  if (card.lost_at) return 'bg-n-ruby-3 text-n-ruby-11';
  return 'bg-n-alpha-1 text-n-slate-11';
};

const statusLabel = card => {
  if (card.won_at) return t('PIPELINE.CARD.STATUS.WON');
  if (card.lost_at) return t('PIPELINE.CARD.STATUS.LOST');
  return t('PIPELINE.CARD.STATUS.OPEN');
};

const resolveStageName = card => {
  if (!card?.pipeline_stage) return '-';
  return resolveCardStageName(
    card,
    store.getters['pipelines/getPipelines'] || [],
    store.getters['pipelines/getSelectedPipeline']
  );
};

const formatCurrency = (value, currency = 'BRL') => {
  if (value === null || value === undefined || value === '') return '';
  const num = parseFloat(value);
  if (Number.isNaN(num)) return '';
  try {
    return new Intl.NumberFormat(getUserLocale(), {
      style: 'currency',
      currency,
    }).format(num);
  } catch {
    return `${value}`;
  }
};

const cardValue = card => getEffectiveCardValue(card);

const cardCurrency = card => {
  const raw =
    card.currency || card.item_details?.currency || accountCurrency.value;
  return typeof raw === 'object' ? raw.code || accountCurrency.value : raw;
};

const generatePipelineSlug = pipeline => {
  if (!pipeline?.id) return null;
  const name = (pipeline.name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return name ? `${pipeline.id}-${name}` : String(pipeline.id);
};

const resolvePipelineForCard = card => {
  const pipelines = store.getters['pipelines/getPipelines'] || [];
  const pipelineId =
    card.pipeline?.id ||
    card.pipeline_id ||
    card.collection?.id ||
    card.collection_id;
  if (!pipelineId) return null;

  return (
    pipelines.find(
      candidate => String(candidate.id) === String(pipelineId)
    ) || {
      id: pipelineId,
      name: card.pipeline?.name || card.collection?.name || '',
    }
  );
};

const relatedCardRoute = card => {
  const pipeline = resolvePipelineForCard(card);
  const pipelineSlug = generatePipelineSlug(pipeline);
  if (!pipelineSlug) return null;

  return {
    name: 'pipeline_item_detail',
    params: {
      accountId: store.getters.getCurrentAccountId,
      pipelineSlug,
      view: 'item',
      itemId: card.id,
    },
  };
};

const relatedCardPipelineName = card =>
  resolvePipelineForCard(card)?.name || '—';

const handleNavigationClick = (event, card) => {
  if (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  ) {
    emit('navigate-to-card', card);
    emit('navigating', card);
  }
};

const formatRelativeTime = dateStr => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const rtf = new Intl.RelativeTimeFormat(getUserLocale(), {
      numeric: 'auto',
    });
    const diffMins = Math.round(diffMs / 60000);
    if (Math.abs(diffMins) < 60) return rtf.format(-diffMins, 'minute');
    const diffHours = Math.round(diffMs / 3600000);
    if (Math.abs(diffHours) < 24) return rtf.format(-diffHours, 'hour');
    const diffDays = Math.round(diffMs / 86400000);
    return rtf.format(-diffDays, 'day');
  } catch {
    return '';
  }
};

const fetchRelatedCards = async ({ append = false } = {}) => {
  if (append && loadingMore.value) return;
  if (append && (!hasMore.value || !nextCursor.value)) return;

  if (!append) {
    cancelRelatedFetch();
    paginationContext = currentFetchContext();
    seenCursors = new Set();
    relatedCards.value = [];
    hasMore.value = false;
    nextCursor.value = null;
  }

  const context = paginationContext;
  if (!context?.accountId || !context.cardId || !context.contactId) {
    loading.value = false;
    loadingMore.value = false;
    error.value = null;
    return;
  }

  const requestId = fetchRequestId;
  const requestCursor = append ? nextCursor.value : null;
  const controller = new AbortController();
  fetchController = controller;
  if (append) loadingMore.value = true;
  else loading.value = true;
  error.value = null;

  try {
    const requestParams = {
      contact_id: context.contactId,
      exclude_id: context.cardId,
      limit: 50,
    };
    if (context.status !== 'all') {
      requestParams.filters = { status: context.status };
    }
    if (requestCursor) requestParams.cursor = requestCursor;

    const { data } = await PipelineAPI.getCards(null, requestParams, {
      accountId: context.accountId,
      signal: controller.signal,
    });
    if (!fetchContextMatches(context, requestId)) return;

    const responseCards = data?.data ?? data;
    const pageCards = Array.isArray(responseCards) ? responseCards : [];
    const cardsById = new Map(
      (append ? relatedCards.value : []).map(card => [card.id, card])
    );
    pageCards.forEach(card => cardsById.set(card.id, card));
    relatedCards.value = [...cardsById.values()];

    if (!data?.meta?.has_more) {
      hasMore.value = false;
      nextCursor.value = null;
      return;
    }

    const responseCursor = data.meta.next_cursor;
    if (!responseCursor || seenCursors.has(responseCursor)) {
      hasMore.value = false;
      nextCursor.value = null;
      error.value = t('PIPELINE.RELATED.LOAD_ERROR');
      return;
    }
    seenCursors.add(responseCursor);
    nextCursor.value = responseCursor;
    hasMore.value = true;
  } catch (requestError) {
    if (controller.signal.aborted || !fetchContextMatches(context, requestId)) {
      return;
    }
    error.value = t('PIPELINE.RELATED.LOAD_ERROR');
    if (!append) relatedCards.value = [];
  } finally {
    if (fetchContextMatches(context, requestId)) {
      if (append) loadingMore.value = false;
      else loading.value = false;
      if (fetchController === controller) fetchController = null;
    }
  }
};

const fetchNextPage = () => fetchRelatedCards({ append: true });

watch(
  () => [
    normalizeContextId(store.getters.getCurrentAccountId),
    normalizeContextId(props.item?.id),
    normalizeContextId(props.item?.contact_id || props.item?.contact?.id),
    filterStatus.value,
  ],
  () => fetchRelatedCards(),
  { immediate: true }
);

onBeforeUnmount(cancelRelatedFetch);
</script>

<template>
  <div class="space-y-4">
    <!-- Header -->
    <div class="flex items-center justify-between gap-2 flex-wrap">
      <h3 class="text-lg font-semibold text-n-slate-12">
        {{ t('PIPELINE.RELATED.TITLE') }}
        <span
          v-if="relatedCards.length"
          class="text-sm font-normal text-n-slate-11"
        >
          {{ `(${displayedCount})` }}
        </span>
      </h3>
      <!-- Status filter -->
      <Select
        v-model="filterStatus"
        :aria-label="t('PIPELINE.RELATED.FILTER_LABEL')"
        :options="filterOptions"
      />
    </div>

    <!-- Error state -->
    <div v-if="error && !relatedCards.length" class="text-center py-8">
      <p class="text-sm text-n-ruby-11">{{ error }}</p>
      <button
        type="button"
        class="mt-3 min-h-11 rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
        @click="fetchRelatedCards"
      >
        {{ t('PIPELINE.RELATED.RETRY') }}
      </button>
    </div>

    <!-- Loading state -->
    <div
      v-else-if="loading && !relatedCards.length"
      class="text-center py-12"
      role="status"
      aria-live="polite"
    >
      <span
        class="inline-block w-8 h-8 border-2 border-n-weak border-t-n-brand rounded-full motion-safe:animate-spin"
        aria-hidden="true"
      />
      <span class="sr-only">{{ t('PIPELINE.LOADING') }}</span>
    </div>

    <!-- Empty state -->
    <div v-else-if="!loading && !relatedCards.length" class="text-center py-12">
      <fluent-icon
        icon="folder-open"
        size="32"
        class="text-n-slate-9 mx-auto"
      />
      <p class="text-sm text-n-slate-10 mt-2">
        {{ t('PIPELINE.RELATED.EMPTY_STATE') }}
      </p>
    </div>

    <!-- Cards list -->
    <div v-else class="space-y-2">
      <article
        v-for="card in relatedCards"
        :key="card.id"
        data-testid="related-card"
        class="bg-n-solid-3 rounded-lg p-4"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="flex-1 min-w-0">
            <!-- Title + status badge -->
            <div class="flex items-center gap-2 mb-1">
              <h4 class="font-medium text-n-slate-12 truncate">
                {{ card.title || t('PIPELINE.UNTITLED_ITEM') }}
              </h4>
              <span
                class="flex-shrink-0 text-xs px-2 py-0.5 rounded-full"
                :class="statusBadgeClass(card)"
              >
                {{ statusLabel(card) }}
              </span>
            </div>

            <!-- Pipeline / stage / value -->
            <div
              class="flex items-center gap-1.5 text-xs text-n-slate-11 flex-wrap"
            >
              <span>{{ relatedCardPipelineName(card) }}</span>
              <span class="i-lucide-dot size-3" aria-hidden="true" />
              <span>{{ resolveStageName(card) }}</span>
              <template v-if="cardValue(card) != null">
                <span class="i-lucide-dot size-3" aria-hidden="true" />
                <span>{{
                  formatCurrency(cardValue(card), cardCurrency(card))
                }}</span>
              </template>
            </div>

            <!-- Owner + relative time -->
            <div class="flex items-center gap-2 mt-2 text-xs text-n-slate-10">
              <Avatar
                :name="card.owner?.name || t('PIPELINE.RELATED.NO_AGENT')"
                :src="card.owner?.avatar_url"
                :size="20"
              />
              <span>{{
                card.owner?.name || t('PIPELINE.RELATED.NO_AGENT')
              }}</span>
              <span class="i-lucide-dot size-3" aria-hidden="true" />
              <span>{{ formatRelativeTime(card.updated_at) }}</span>
            </div>
          </div>

          <!-- View button -->
          <RouterLink
            v-if="relatedCardRoute(card)"
            :to="relatedCardRoute(card)"
            class="flex-shrink-0 min-h-11 px-3 py-2 text-sm font-medium bg-n-brand text-white rounded-lg hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2"
            @click="handleNavigationClick($event, card)"
          >
            {{ t('PIPELINE.RELATED.VIEW_ITEM') }}
          </RouterLink>
        </div>
      </article>
    </div>

    <div
      v-if="error && relatedCards.length"
      class="rounded-lg border border-n-ruby-7 bg-n-ruby-3 p-3 text-center"
      role="alert"
    >
      <p class="text-sm text-n-ruby-11">{{ error }}</p>
      <button
        type="button"
        class="mt-2 min-h-11 rounded-lg px-3 text-sm font-medium text-n-ruby-11 hover:bg-n-ruby-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
        @click="fetchNextPage"
      >
        {{ t('PIPELINE.RELATED.RETRY') }}
      </button>
    </div>

    <button
      v-if="hasMore && !error"
      type="button"
      class="min-h-11 w-full rounded-lg border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:cursor-not-allowed disabled:opacity-50"
      :disabled="loadingMore"
      @click="fetchNextPage"
    >
      {{ loadingMore ? t('PIPELINE.LOADING') : t('PIPELINE.LOAD_MORE_SIMPLE') }}
    </button>
  </div>
</template>
